/** Ported 1:1 from app/templates/components/utils/moment-utils.js.html
 *  (Phase 4 of the port). Shared formatting for moments: type/time/player/
 *  score/zone labels, the timeline tooltip HTML and the field-map popover
 *  HTML. Same behavior, module exports instead of window.MomentUtils.
 */

export type MomentLike = {
  id?: string | number
  frame?: number
  score?: number | null
  /** 'press_score' on a press scored by the pressing model. */
  score_scale?: string | null
  /** That scale's bands, as the server sends them with such a press. */
  score_bands?: { okay: number; good: number } | null
  type?: string
  moment_type?: string
  zone?: string
  player?: string | number | null
  to_player?: string | number | null
  player_id?: string | number | null
  target_player_id?: string | number | null
  x?: number
  y?: number
  /** The half, 1 or 2; null when none is stored. */
  phase?: number | null
}

/** 'progressive_pass' → 'Progressive Pass' (with the two special cases). */
export function formatType(type: string | undefined): string {
  if (!type) return 'Moment'
  const typeLabels: Record<string, string> = {
    box_entry_cci: 'Chance Creation',
    spatial_xg: 'Spatial xG',
  }
  const special = typeLabels[type]
  if (special) return special
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/** Frame → MM:SS (zero-padded minutes), at the game's frame rate as the
 *  server sends it — no default: a 30 fps guess put every 25 fps game's
 *  times 20% early (defect 13). '-' when the server has no rate: no time
 *  is better than an invented one. The one clock of these pages — the
 *  timeline's scrubber had its own M:SS copy. */
export function formatTime(frame: number, fps: number | null): string {
  if (!fps) return '-'
  const seconds = Math.floor(frame / fps)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/** "29 → 30" for passes, "29" for runs, null when no player id at all. */
export function formatPlayer(moment: MomentLike | null): string | null {
  if (!moment) return null
  const extractNumber = (playerId: unknown): string | null => {
    if (!playerId) return null
    const match = String(playerId).match(/player(\d+)/i)
    return match ? match[1]! : String(playerId)
  }
  const fromPlayer = extractNumber(moment.player_id ?? moment.player)
  const toPlayer = extractNumber(moment.target_player_id ?? moment.to_player)
  if (!fromPlayer) return null
  if (toPlayer) return `${fromPlayer} → ${toPlayer}`
  return fromPlayer
}

/** 0.45 → '45'; null/undefined → null. */
export function formatScore(score: number | null | undefined): string | null {
  if (score === null || score === undefined) return null
  return String(Math.round(score * 100))
}

/** Zone code → moment-card style label. */
export function formatZone(zone: string | undefined): string {
  const zones: Record<string, string> = {
    scoring: 'Attacking',
    middle: 'Middle-ATT',
    'mid-att': 'Middle-ATT',
    'mid-def': 'Middle-DEF',
    attacking: 'Attacking',
    defensive: 'Defensive',
  }
  return zones[zone ?? ''] ?? zone ?? ''
}

/** Phosphor icon class for a moment type. */
export function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    progressive_pass: 'ph-arrow-bend-up-right',
    supportive_pass: 'ph-arrow-right',
    progressive_run: 'ph-person-simple-run',
    supportive_run: 'ph-person-simple-walk',
    pressing: 'ph-shield-chevron',
    spatial_xg: 'ph-crosshair-simple',
    box_entry_cci: 'ph-crosshair-simple',
    high_press: 'ph-shield-chevron',
  }
  return icons[type] ?? 'ph-circle'
}

/** Rule-based one-line insight for the popover footer. */
export function generateInsight(moment: MomentLike): string {
  const type = moment.type ?? moment.moment_type ?? ''
  const score = moment.score ?? 0
  const zone = moment.zone ?? ''
  const scorePercent = Math.round(score * 100)

  const quality = scorePercent >= 70 ? 'High-quality'
    : scorePercent >= 50 ? 'Effective' : 'Developing'

  const zoneInsightMap: Record<string, string> = {
    scoring: 'the final third',
    attacking: 'the attacking third',
    middle: 'the middle third',
    'mid-att': 'the attacking half',
    'mid-def': 'the defensive half',
    defensive: 'the defensive third',
  }
  const zoneDesc = zoneInsightMap[zone] ?? 'the pitch'

  if (type.includes('pass')) return `${quality} forward progression into ${zoneDesc}`
  if (type.includes('run')) return `${quality} ball carry advancing into ${zoneDesc}`
  if (type === 'pressing' || type === 'high_press') return `Pressing action applied in ${zoneDesc}`
  if (type === 'spatial_xg') return `Goal-scoring opportunity created in ${zoneDesc}`
  if (type === 'box_entry_cci') return `${quality} chance created in the penalty area`
  return ''
}

/** Tooltip HTML for a timeline dot (data-tooltip classes). */
export function buildTooltipHTML(moment: MomentLike | null, fps: number | null): string {
  if (!moment) return ''

  const typeLabel = formatType(moment.type ?? moment.moment_type)
  const time = formatTime(moment.frame ?? 0, fps)
  const player = formatPlayer(moment)
  const score = formatScore(moment.score)

  let html = `<div class="data-tooltip__header">${typeLabel}</div>`
  html += `<div class="data-tooltip__row">
      <span class="data-tooltip__label">Time</span>
      <span class="data-tooltip__value">${time}</span>
    </div>`
  if (player) {
    const playerLabel = (moment.target_player_id ?? moment.to_player) ? 'Players' : 'Player'
    html += `<div class="data-tooltip__row">
        <span class="data-tooltip__label">${playerLabel}</span>
        <span class="data-tooltip__value">${player}</span>
      </div>`
  }
  if (score !== null) {
    html += `<div class="data-tooltip__row">
        <span class="data-tooltip__label">Score</span>
        <span class="data-tooltip__value">${score}</span>
      </div>`
  }
  return html
}

/** Popover HTML for a field-map dot hover (compact moment card style). */
export function buildPopoverHTML(moment: MomentLike | null, fps: number | null): string {
  if (!moment) return ''

  const type = moment.type ?? moment.moment_type ?? 'moment'
  const typeLabel = formatType(type)
  const typeIcon = getTypeIcon(type)
  const time = formatTime(moment.frame ?? 0, fps)
  const zone = formatZone(moment.zone)
  const insight = generateInsight(moment)

  const player = moment.player
  const toPlayer = moment.to_player
  const hasPlayer = player !== null && player !== undefined
  const hasTarget = toPlayer !== null && toPlayer !== undefined

  let html = `
      <div class="moment-popover__header">
        <div class="moment-popover__badge">
          <i class="ph ${typeIcon} moment-popover__badge-icon"></i>
          <span>${typeLabel}</span>
        </div>
        <button class="moment-popover__play" title="View moment">
          <i class="ph ph-play-circle"></i>
        </button>
      </div>
      <div class="moment-popover__body">
        <div class="moment-popover__ring">
          <div class="score-ring score-ring--xs" id="popover-ring"></div>
        </div>
        <div class="moment-popover__meta">`

  if (hasPlayer) {
    const playerText = hasTarget ? `#${player} → #${toPlayer}` : `#${player}`
    html += `
          <div class="moment-popover__meta-row">
            <i class="ph ph-user"></i>
            <span class="moment-popover__meta-value">${playerText}</span>
          </div>`
  }

  html += `
          <div class="moment-popover__meta-row">
            <i class="ph ph-clock"></i>
            <span class="moment-popover__meta-value">${time}</span>
          </div>`

  if (zone) {
    html += `
          <div class="moment-popover__meta-row">
            <i class="ph ph-map-pin"></i>
            <span class="moment-popover__meta-value">${zone}</span>
          </div>`
  }

  html += `
        </div>
      </div>`

  if (insight) {
    html += `
      <div class="moment-popover__insight">${insight}</div>`
  }

  return html
}
